// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/features/cofre/faturamento.ts
//  Tipos do faturamento do Cofre Fiscal (CofreFaturamentoService na API): carteira do
//  contador, faturamento de um cliente, projeção do Simples e Simples × Presumido.
//  A mesma resposta serve ao Portal do Contador e à Central Fiscal do cliente no ERP.
// ════════════════════════════════════════════════════════════════════════════

export type SituacaoSimples = 'ok' | 'atencao' | 'sublimite' | 'limite' | 'fora';

export interface MarcoSimples {
  chave: 'sublimite' | 'sublimite-excedido' | 'limite' | 'limite-excedido';
  valor: number;
  rotulo: string;
  atingidoEm?: number | null;
  previstoEm?: number | null;
}

export interface ProjecaoSimples {
  ano: number;
  receitaAno: number;
  mediaMensal: number;
  mesesNaMedia: number;
  projecaoAno: number;
  situacao: SituacaoSimples;
  mensagem?: string | null;
  marcos: MarcoSimples[];
}

export interface CarteiraCliente {
  contribuinteId: number;
  cnpjCpf: string;
  razaoSocial?: string | null;
  uf?: string | null;
  crt?: number | null;
  receitaMes: number;
  receitaMesAnterior: number;
  rbt12: number;
  mesesComDados: number;
  projecao: ProjecaoSimples;
  documentosMes: number;
  canceladasMes: number;
  mesesSemDocumento: number;
  ultimoRecebido?: string | null;
}

export interface FaturamentoMes {
  competencia: number;
  producao: number;
  revenda: number;
  servico: number;
  devolucoes: number;
  documentos: number;
  liquida: number;
  ticketMedio: number;
}

export interface Fatia { chave: string; rotulo: string; valor: number; percentual: number }

export interface Faturamento {
  competencia: number;
  crt?: number | null;
  meses: FaturamentoMes[];
  receita12: number;
  receita12Anterior: number;
  composicao: Fatia[];
  porUf: Fatia[];
  porTipoCliente: Fatia[];
  clientes: { cnpjCpf: string; nome?: string | null; tipo: 'empresa' | 'pessoa'; documentos: number; valor: number; percentual: number }[];
  produtos: { codigo?: string | null; descricao?: string | null; ncm?: string | null; quantidade: number; valor: number; percentual: number }[];
  projecao: ProjecaoSimples;
  observacao?: string | null;
}

export interface ComparacaoRegime {
  receitaComercio: number;
  receitaServico: number;
  receita: number;
  mesesComDados: number;
  anexo: number;
  nomeAnexo: string;
  das: number;
  aliquotaMediaSimples: number;
  encargosForaDoDas: number;
  irpj: number;
  csll: number;
  pis: number;
  cofins: number;
  icmsIss: number;
  encargosFolha: number;
  totalSimples: number;
  totalPresumido: number;
  diferenca: number;
  conclusao: string;
  premissas: string[];
}

export interface ParametrosRegime { anexo?: number; icmsIss?: number; folha?: number }

export const ROTULO_SITUACAO_SIMPLES: Record<SituacaoSimples, string> = {
  ok: 'Dentro do Simples',
  atencao: 'Perto do sublimite',
  sublimite: 'Acima do sublimite',
  limite: 'Acima do limite',
  fora: 'Fora do Simples',
};

/** Classes do selo de cada situação (a cor carrega o risco: verde, âmbar, laranja, vermelho). */
export const COR_SITUACAO_SIMPLES: Record<SituacaoSimples, string> = {
  ok: 'border-green-600 text-green-700 dark:text-green-400',
  atencao: 'border-amber-500 text-amber-700 dark:text-amber-400',
  sublimite: 'border-orange-500 text-orange-700 dark:text-orange-400',
  limite: 'border-red-500 text-red-700 dark:text-red-400',
  fora: 'border-red-600 bg-red-600 text-white',
};

/** Ordem de gravidade, para ordenar a carteira pelo risco. */
export const GRAVIDADE_SIMPLES: Record<SituacaoSimples, number> = { fora: 4, limite: 3, sublimite: 2, atencao: 1, ok: 0 };

/** CRT 3: regime normal — a projeção do Simples não se aplica. */
export const ehRegimeNormal = (crt?: number | null) => crt === 3;

/** "out/26" */
export const rotuloMesCurto = (c: number) =>
  `${new Date(Math.floor(c / 100), (c % 100) - 1, 1).toLocaleDateString('pt-BR', { month: 'short' }).replace('.', '')}/${String(Math.floor(c / 100)).slice(2)}`;

export const percentual = (v: number) => `${v.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;

/** Variação de b para a, em %; nulo quando b é zero. */
export const variacao = (a: number, b: number) => (b ? ((a - b) / Math.abs(b)) * 100 : null);
